<template>
  <SidebarTabTemplate
    :title="$t('workflowApps.title')"
    class="workflow-apps-sidebar-tab bg-(--p-tree-background)"
  >
    <template #tool-buttons>
      <Button
        v-if="hasInputs && !isEditMode"
        v-tooltip.bottom="$t('workflowApps.edit')"
        icon="pi pi-pencil"
        severity="secondary"
        text
        @click="startEditMode"
      />
      <Button
        v-if="isEditMode"
        v-tooltip.bottom="$t('workflowApps.save')"
        icon="pi pi-check"
        severity="secondary"
        text
        @click="saveAllChanges"
      />
      <Button
        v-if="isEditMode"
        v-tooltip.bottom="$t('workflowApps.cancel')"
        icon="pi pi-times"
        severity="secondary"
        text
        @click="cancelAllChanges"
      />
      <Button
        v-tooltip.bottom="$t('workflowApps.addInput')"
        :icon="isAddingMode ? 'pi pi-times' : 'pi pi-plus'"
        severity="secondary"
        text
        @click="toggleAddingMode"
      />
      <Button
        v-tooltip.bottom="$t('workflowApps.publish.title')"
        icon="pi pi-upload"
        severity="success"
        text
        @click="goToPublish"
      />
    </template>
    <template #body>
      <div class="workflow-apps-content p-4">
        <div v-if="isAddingMode" class="mb-4 p-4 rounded-xl adding-mode-tip">
          <div class="flex items-center gap-3 adding-mode-content">
            <i class="pi pi-mouse-pointer text-lg animate-pulse"></i>
            <div>
              <span class="text-sm font-semibold block">{{
                $t('workflowApps.clickToAddInputs')
              }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="!hasInputs && !isAddingMode"
          class="text-center text-surface-500 py-12"
        >
          <div
            class="bg-transparent rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
          >
            <i class="pi pi-info-circle text-2xl text-surface-400"></i>
          </div>
          <p class="font-medium text-surface-600 mb-2">
            {{ $t('workflowApps.noInputsFound') }}
          </p>
          <p class="text-sm text-surface-500">
            {{ $t('workflowApps.clickPlusButtonToAddInputs') }}
          </p>
        </div>

        <!-- 使用 WorkflowAppForm 组件 - 只在有输入时渲染 -->
        <WorkflowAppForm
          v-if="hasInputs"
          :workflow-inputs="workflowInputs"
          :input-values="inputValues"
          :is-edit-mode="isEditMode"
          :share-id="currentShareId"
          @input-change="handleInputChange"
          @remove-input="removeInput"
          @save-input-name="saveInputName"
          @save-input-config="handleInputConfigChange"
          @save-input-type="handleInputTypeChange"
          @update-input-id="handleInputIdUpdate"
        />
      </div>

      <!-- 编辑模式控制按钮 -->
      <div v-if="isEditMode" class="mt-4 p-4 border-t border-surface-200">
        <div class="flex gap-2 justify-end">
          <Button
            :label="$t('workflowApps.cancel')"
            severity="secondary"
            size="small"
            @click="cancelAllChanges"
          />
          <Button
            :label="$t('workflowApps.save')"
            severity="primary"
            size="small"
            @click="saveAllChanges"
          />
        </div>
        <div class="text-xs text-surface-500 mt-2 text-center">
          {{ $t('workflowApps.editModeDescription') }}
        </div>
      </div>
    </template>
  </SidebarTabTemplate>

  <!-- 图片选择弹窗现在由 WorkflowAppForm 组件处理 -->

  <!-- 发布工作流应用弹窗 -->
  <WorkflowAppPublishDialog
    v-model:visible="showPublishDialog"
    @success="handlePublishSuccess"
  />
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

// ImageSelectorDialog 现在由 WorkflowAppForm 组件处理
import WorkflowAppPublishDialog from '@/components/dialogs/WorkflowAppPublishDialog.vue'
import SidebarTabTemplate from '@/components/sidebar/tabs/SidebarTabTemplate.vue'
import WorkflowAppForm from '@/components/workflow/WorkflowAppForm.vue'
import { useWorkflowStore } from '@/platform/workflow/management/stores/workflowStore'
import type { WorkflowAppConfig } from '@/platform/workflow/validation/schemas/workflowSchema'
import { app as comfyApp } from '@/scripts/app'

interface WorkflowInput {
  id: string
  name: string
  type: string
  value: any
  description?: string
  placeholder?: string
  options?: any[]
  order?: number
  config: {
    min?: number
    max?: number
    step?: number
    defaultValue?: any
    required?: boolean
    order?: number
    llmPrompt?: string
    // 方便后期扩展其他配置字段
    [key: string]: any
  }
}

// const { t } = useI18n() // 现在由 WorkflowAppForm 组件处理
const workflowStore = useWorkflowStore()

// 输入类型选项 - 现在由 WorkflowAppForm 组件处理

const inputValues = ref<Record<string, any>>({})
const isAddingMode = ref(false)
const selectedInputs = ref<Set<string>>(new Set())
const isEditMode = ref(false)
const newlyAddedInputs = ref<Set<string>>(new Set()) // 跟踪新添加的输入（用于自动类型检测）
const originalInputs = ref<Map<string, any>>(new Map()) // 保存原始状态
const pendingChanges = ref<Map<string, any>>(new Map()) // 保存待处理的修改
const inputIdMapping = ref<Map<string, string>>(new Map()) // 保存 inputId 的映射关系（旧ID -> 新ID）
const editingInputId = ref<string | null>(null)
const editingInputName = ref('')

// 当前工作流应用的share_id
const currentShareId = computed(() => {
  const activeWorkflow = workflowStore.activeWorkflow
  if (!activeWorkflow) return undefined

  // 从 extra.share_id 获取
  const extra = activeWorkflow.activeState?.extra as any
  if (extra?.share_id) {
    return extra.share_id
  }

  return undefined
})

// 图片选择弹窗相关状态现在由 WorkflowAppForm 组件处理

// 发布弹窗状态
const showPublishDialog = ref(false)

const workflowInputs = computed<WorkflowInput[]>(() => {
  const activeWorkflow = workflowStore.activeWorkflow
  if (!activeWorkflow) return []

  const inputs: WorkflowInput[] = []
  const configInputs = new Map<string, any>()

  // 首先从 workflowApp 配置中获取已保存的输入参数
  if (activeWorkflow.activeState.extra?.workflowApp) {
    const config = activeWorkflow.activeState.extra
      .workflowApp as WorkflowAppConfig

    if (config.inputs && config.inputs.length > 0) {
      config.inputs.forEach((savedInput) => {
        configInputs.set(savedInput.inputId, savedInput)
      })
    }
  }

  // 从工作流节点中获取当前选中的输入参数
  const nodes = comfyApp.graph?._nodes || []

  // 处理当前工作流中存在的节点
  nodes.forEach((node) => {
    if (node.widgets && node.widgets.length > 0) {
      node.widgets.forEach((widget) => {
        if (widget.name && widget.type) {
          const inputId = `${node.id}-${widget.name}`
          if (selectedInputs.value.has(inputId)) {
            // 检查是否被软删除
            const isSoftDeleted =
              pendingChanges.value.get(inputId)?.deleted === true
            if (isSoftDeleted) {
              return // 跳过软删除的输入
            }

            // 检查是否有待处理的修改
            const pendingChange = pendingChanges.value.get(inputId)
            const savedInput = configInputs.get(inputId)

            if (pendingChange) {
              // 如果有待处理的修改，使用修改后的数据
              const baseInput = savedInput || {
                inputId,
                nodeId: inputId.split('-')[0],
                widgetName: inputId.split('-')[1],
                name: widget.name,
                type: widget.type,
                value: widget.value,
                description: (widget.options as any)?.description,
                placeholder: getPlaceholder(widget.type),
                options: getOptions(widget.type, widget.options),
                config: {
                  min: widget.options?.min,
                  max: widget.options?.max,
                  step: widget.options?.step,
                  defaultValue: widget.value,
                  required: false,
                  order: 0
                }
              }

              inputs.push({
                id: inputId,
                name: pendingChange.name || baseInput.name,
                type: baseInput.type,
                value: baseInput.value,
                description: baseInput.description,
                placeholder: baseInput.placeholder,
                options: baseInput.options,
                order:
                  pendingChange.order ||
                  baseInput.config?.order ||
                  baseInput.order ||
                  0,
                config: {
                  min: baseInput.config?.min ?? baseInput.min,
                  max: baseInput.config?.max ?? baseInput.max,
                  step: baseInput.config?.step ?? baseInput.step,
                  defaultValue:
                    baseInput.config?.defaultValue ?? baseInput.value,
                  required: baseInput.config?.required ?? false,
                  order:
                    pendingChange.order ||
                    baseInput.config?.order ||
                    baseInput.order ||
                    0,
                  llmPrompt: baseInput.config?.llmPrompt ?? ''
                }
              })
            } else if (savedInput) {
              // 使用配置中的数据
              // 对于已保存的配置，直接使用保存的类型，不进行自动检测
              // 这样可以确保用户手动设置的类型（如 file.video）不会被覆盖
              const finalType = savedInput.type

              // 如果配置中的选项为空，尝试从原始节点获取
              let options = savedInput.options
              if (!options || options.length === 0) {
                options = getOptions(finalType, widget.options || {})
              }

              inputs.push({
                id: savedInput.inputId,
                name: savedInput.name,
                type: finalType,
                value: savedInput.value,
                description: savedInput.description,
                placeholder:
                  savedInput.placeholder || getPlaceholder(finalType),
                options: options,
                order: savedInput.config?.order || savedInput.order || 0,
                config: {
                  min: savedInput.config?.min ?? savedInput.min,
                  max: savedInput.config?.max ?? savedInput.max,
                  step: savedInput.config?.step ?? savedInput.step,
                  defaultValue:
                    savedInput.config?.defaultValue ?? savedInput.value,
                  required: savedInput.config?.required ?? false,
                  order: savedInput.config?.order ?? savedInput.order ?? 0,
                  llmPrompt: savedInput.config?.llmPrompt ?? ''
                }
              })
            } else {
              // 使用节点数据
              // 检查选项是否包含图片文件
              const hasImageFiles = (options: any): boolean => {
                if (!options) return false
                const optionArray = Array.isArray(options) ? options : [options]
                return optionArray.some((option) => {
                  const value =
                    typeof option === 'string' ? option : option.value
                  return (
                    value && /\.(png|jpg|jpeg|gif|bmp|webp|svg)$/i.test(value)
                  )
                })
              }

              // 只有在添加输入时才自动检测类型，已保存的输入保持原类型
              // 检查是否是新添加的输入
              const isNewlyAdded = newlyAddedInputs.value.has(inputId)
              // 如果选项包含图片文件，将类型设置为file.image（仅对新添加的输入）
              // 但如果节点类型是文件类型（file.video、file.audio），保持原类型不变
              const finalType =
                widget.type === 'file.video' || widget.type === 'file.audio'
                  ? widget.type // 保持视频或音频类型不变
                  : isNewlyAdded && hasImageFiles(widget.options)
                    ? 'file.image' // 只对新添加的输入进行自动检测
                    : widget.type

              inputs.push({
                id: inputId,
                name: widget.name,
                type: finalType,
                value: widget.value,
                description: (widget.options as any)?.description,
                placeholder: getPlaceholder(finalType),
                options: getOptions(finalType, widget.options),
                order: 0,
                config: {
                  min: widget.options?.min,
                  max: widget.options?.max,
                  step: widget.options?.step,
                  defaultValue: widget.value,
                  required: false,
                  order: 0,
                  llmPrompt: ''
                }
              })
            }
          }
        }
      })
    }
  })

  // 处理配置中存在但节点已不存在的输入项
  const processedInputIds = new Set(inputs.map((input) => input.id))
  configInputs.forEach((savedInput, inputId) => {
    if (selectedInputs.value.has(inputId) && !processedInputIds.has(inputId)) {
      // 检查是否被软删除
      const isSoftDeleted = pendingChanges.value.get(inputId)?.deleted === true
      if (isSoftDeleted) {
        return
      }

      inputs.push({
        id: savedInput.inputId,
        name: savedInput.name,
        type: savedInput.type,
        value: savedInput.value,
        description: savedInput.description,
        placeholder: savedInput.placeholder || getPlaceholder(savedInput.type),
        options: savedInput.options || [],
        order: savedInput.config?.order || savedInput.order || 0,
        config: {
          min: savedInput.config?.min ?? savedInput.min,
          max: savedInput.config?.max ?? savedInput.max,
          step: savedInput.config?.step ?? savedInput.step,
          defaultValue: savedInput.config?.defaultValue ?? savedInput.value,
          required: savedInput.config?.required ?? false,
          order: savedInput.config?.order ?? savedInput.order ?? 0,
          llmPrompt: savedInput.config?.llmPrompt ?? ''
        }
      })
    }
  })

  // 按照order字段排序，如果没有order则按原始顺序
  inputs.sort((a, b) => {
    const orderA = a.order || 0
    const orderB = b.order || 0
    return orderA - orderB
  })

  return inputs
})

const hasInputs = computed(() => workflowInputs.value.length > 0)

// 这些方法现在由 WorkflowAppForm 组件处理
// 但我们需要保留一些辅助方法

const getPlaceholder = (type: string): string => {
  switch (type) {
    case 'string':
    case 'text':
    case 'customtext':
    case 'multiline':
      return '输入文本'
    case 'number':
    case 'int':
    case 'float':
      return '输入数字'
    case 'boolean':
      return '选择布尔值'
    default:
      return '输入值'
  }
}

const getOptions = (type: string, options: any): any[] => {
  // 处理combo类型的选项，支持多种数据结构
  if (type === 'combo') {
    let choices = null

    // 尝试不同的选项数据结构
    if (options?.choices) {
      choices = options.choices
    } else if (options?.values) {
      choices = options.values
    } else if (Array.isArray(options)) {
      choices = options
    }

    if (choices && Array.isArray(choices)) {
      return choices.map((choice: any) => ({
        label: choice,
        value: choice
      }))
    }
  }

  // 处理其他类型的选项
  if (options?.choices) {
    return options.choices.map((choice: any) => ({
      label: choice,
      value: choice
    }))
  }

  if (options?.values) {
    return options.values.map((choice: any) => ({
      label: choice,
      value: choice
    }))
  }

  return []
}

// 处理输入框值变化
const handleInputChange = (inputId: string, value: any) => {
  // 更新本地值
  inputValues.value[inputId] = value

  // 自动同步到工作流节点
  updateInputValue(inputId, value)

  // 只有在非编辑模式下才自动保存
  if (!isEditMode.value) {
    saveWorkflowAppConfig()
  }
}

// 图片相关方法现在由 WorkflowAppForm 组件处理

// 调试函数现在由 WorkflowAppForm 组件处理

// 配置相关方法现在由 WorkflowAppForm 组件处理

const updateInputValue = (inputId: string, value: any) => {
  inputValues.value[inputId] = value

  // 更新对应的工作流节点
  const [nodeId, widgetName] = inputId.split('-')

  const node = comfyApp.graph?._nodes?.find(
    (n) => String(n.id) === String(nodeId)
  )

  if (node && node.widgets) {
    const widget = node.widgets.find((w) => w.name === widgetName)

    if (widget) {
      widget.value = value

      if (widget.callback) {
        widget.callback(value)
      }

      // 强制触发节点重新渲染
      if ((node as any).onDraw) {
        ;(node as any).onDraw()
      }

      // 如果节点有更新方法，调用它
      if ((node as any).setDirtyCanvas) {
        ;(node as any).setDirtyCanvas(true)
      }
    }
  }

  // 只有在非编辑模式且非添加模式下才标记工作流为已修改
  if (!isEditMode.value && !isAddingMode.value) {
    const activeWorkflow = workflowStore.activeWorkflow
    if (activeWorkflow) {
      activeWorkflow.isModified = true

      // 强制触发ChangeTracker检查
      if (activeWorkflow.changeTracker) {
        activeWorkflow.changeTracker.updateModified()
      }
    }
  }
}

// 删除输入参数
const removeInput = (inputId: string) => {
  if (isEditMode.value) {
    // 编辑模式下的软删除
    pendingChanges.value.set(inputId, { deleted: true })
  } else {
    // 非编辑模式下的真删除
    // 从选择的输入中移除
    selectedInputs.value.delete(inputId)

    // 从输入值中移除
    delete inputValues.value[inputId]

    // 立即保存配置
    saveWorkflowAppConfig()
  }
}

// 开始编辑模式
const startEditMode = () => {
  isEditMode.value = true

  // 保存原始状态
  originalInputs.value.clear()
  pendingChanges.value.clear()

  // 保存所有当前输入的状态
  workflowInputs.value.forEach((input) => {
    originalInputs.value.set(input.id, {
      name: input.name,
      value: input.value,
      description: input.description,
      placeholder: input.placeholder,
      options: input.options,
      order: input.order,
      config: input.config
    })
  })
}

// 结束编辑模式
const endEditMode = () => {
  isEditMode.value = false
  editingInputId.value = null
  editingInputName.value = ''
  originalInputs.value.clear()
  pendingChanges.value.clear()
}

// 编辑输入名称方法现在由 WorkflowAppForm 组件处理
// @ts-expect-error - Reserved for future use

const editInputName = (inputId: string) => {
  if (!isEditMode.value) {
    startEditMode()
  }

  const input = workflowInputs.value.find((i) => i.id === inputId)
  if (input) {
    editingInputId.value = inputId
    editingInputName.value = input.name
  }
}

// 保存输入名称（编辑模式下的临时保存）
const saveInputName = (inputId: string, name?: string) => {
  // 优先使用传递的名称参数，如果没有则从 workflowInputs 中获取
  const newName =
    name?.trim() ||
    workflowInputs.value.find((i) => i.id === inputId)?.name?.trim()

  if (newName) {
    // 在编辑模式下，只保存到pendingChanges，不立即保存到工作流
    pendingChanges.value.set(inputId, {
      name: newName
    })
  }
}

// 处理输入配置变化
const handleInputConfigChange = (inputId: string) => {
  const input = workflowInputs.value.find((i) => i.id === inputId)
  if (input) {
    // 在编辑模式下，保存配置到pendingChanges
    if (isEditMode.value) {
      const currentChanges = pendingChanges.value.get(inputId) || {}
      pendingChanges.value.set(inputId, {
        ...currentChanges,
        config: input.config,
        options: input.options // 同时保存options
      })
    } else {
      // 非编辑模式下，立即保存
      saveWorkflowAppConfig()
    }
  }
}

// 处理输入类型变化
const handleInputTypeChange = (inputId: string) => {
  const input = workflowInputs.value.find((i) => i.id === inputId)
  if (input) {
    // 在编辑模式下，保存类型到pendingChanges
    if (isEditMode.value) {
      const currentChanges = pendingChanges.value.get(inputId) || {}
      pendingChanges.value.set(inputId, {
        ...currentChanges,
        type: input.type,
        value: input.value // 同时保存转换后的值
      })
    } else {
      // 非编辑模式下，立即保存
      saveWorkflowAppConfig()
    }
  }
}

// 处理输入ID更新
const handleInputIdUpdate = (
  oldInputId: string,
  newInputId: string,
  nodeId: string,
  widgetName: string
) => {
  // 记录 inputId 的映射关系
  inputIdMapping.value.set(oldInputId, newInputId)

  // 在编辑模式下，保存到pendingChanges
  if (isEditMode.value) {
    // 获取旧的待处理修改
    const oldChanges = pendingChanges.value.get(oldInputId) || {}

    // 将旧的修改迁移到新的 inputId
    pendingChanges.value.set(newInputId, {
      ...oldChanges,
      inputId: newInputId,
      nodeId: nodeId,
      widgetName: widgetName
    })

    // 删除旧的待处理修改
    pendingChanges.value.delete(oldInputId)

    // 更新 selectedInputs
    if (selectedInputs.value.has(oldInputId)) {
      selectedInputs.value.delete(oldInputId)
      selectedInputs.value.add(newInputId)
    }

    // 迁移 inputValues
    if (inputValues.value[oldInputId] !== undefined) {
      inputValues.value[newInputId] = inputValues.value[oldInputId]
      delete inputValues.value[oldInputId]
    }
  }
}

// 保存所有修改（真正的保存）
const saveAllChanges = async () => {
  const activeWorkflow = workflowStore.activeWorkflow
  if (!activeWorkflow) return

  // 确保activeState和extra.workflowApp存在
  if (!activeWorkflow.activeState) {
    activeWorkflow.activeState = {} as any
  }
  if (!activeWorkflow.activeState.extra) {
    activeWorkflow.activeState.extra = {}
  }
  if (!activeWorkflow.activeState.extra.workflowApp) {
    // 如果没有配置，创建一个新的（避免触发workflowInputs重新计算）
    const nodes = comfyApp.graph?._nodes || []
    const inputs = []
    for (const node of nodes) {
      if (node.widgets && node.widgets.length > 0) {
        for (const widget of node.widgets) {
          if (
            widget.name &&
            widget.type &&
            selectedInputs.value.has(`${node.id}-${widget.name}`)
          ) {
            const inputId = `${node.id}-${widget.name}`
            inputs.push({
              nodeId: node.id.toString(),
              widgetName: widget.name,
              inputId: inputId,
              name: widget.name,
              type: widget.type,
              value: inputValues.value[inputId] ?? widget.value,
              description: (widget.options as any)?.description || '',
              placeholder: getPlaceholder(widget.type),
              options: getOptions(widget.type, widget.options),
              min: widget.options?.min,
              max: widget.options?.max,
              step: widget.options?.step
            })
          }
        }
      }
    }

    activeWorkflow.activeState.extra.workflowApp = { inputs }
  }

  // 应用所有待处理的修改
  const config = activeWorkflow.activeState.extra
    .workflowApp as WorkflowAppConfig

  // 首先处理 inputId 的变更：更新 config.inputs 中的 inputId
  inputIdMapping.value.forEach((newInputId, oldInputId) => {
    const input = config.inputs.find((input) => input.inputId === oldInputId)
    if (input) {
      // 更新 inputId、nodeId 和 widgetName
      const changes = pendingChanges.value.get(newInputId) || {}
      input.inputId = newInputId
      input.nodeId = changes.nodeId || input.nodeId
      input.widgetName = changes.widgetName || input.widgetName
    }
  })

  // 处理软删除的项目
  pendingChanges.value.forEach((changes, inputId) => {
    if (changes.deleted === true) {
      // 软删除：从selectedInputs和inputValues中移除
      selectedInputs.value.delete(inputId)
      delete inputValues.value[inputId]
    } else {
      // 普通修改：应用到配置
      // 注意：pendingChanges 中的 key 已经是新的 inputId（因为 handleInputIdUpdate 已经迁移了）
      // 所以直接使用 inputId 查找即可
      const input = config.inputs.find((input) => input.inputId === inputId)

      if (input) {
        // 处理 name 字段（优先处理，确保名称被正确更新）
        if (changes.name !== undefined) {
          input.name = changes.name
        }

        // 处理配置字段的特殊合并
        if (changes.config) {
          if (!input.config) {
            input.config = {}
          }
          Object.assign(input.config, changes.config)
        }

        // 处理options字段
        if (changes.options) {
          input.options = changes.options
        }

        // 处理其他字段（排除name、config和options）
        const {
          name: __name,
          config: _,
          options: __,
          ...otherChanges
        } = changes
        Object.assign(input, otherChanges)
      }
    }
  })

  // 清空 inputId 映射（已处理完成）
  inputIdMapping.value.clear()

  // 直接保存配置
  doSaveWorkflowAppConfig()

  // 强制触发响应式更新：创建新的对象引用
  if (activeWorkflow.activeState.extra?.workflowApp) {
    activeWorkflow.activeState.extra.workflowApp = {
      ...activeWorkflow.activeState.extra.workflowApp,
      inputs: [...config.inputs]
    }
  }

  // 标记工作流为已修改
  activeWorkflow.isModified = true

  // 调用ChangeTracker检测变化
  if (activeWorkflow.changeTracker) {
    activeWorkflow.changeTracker.checkState()
  }

  // 结束编辑模式
  endEditMode()

  // 自动保存工作流
  try {
    await workflowStore.saveWorkflow(activeWorkflow)
  } catch (error) {
    // 静默处理错误
  }
}

// 取消所有修改
const cancelAllChanges = () => {
  // 结束编辑模式
  endEditMode()
}

// 弹窗监控相关变量
let dialogObserver: MutationObserver | null = null
let dialogCheckInterval: number | null = null

// 启动弹窗监控
const startDialogMonitoring = () => {
  // 使用 MutationObserver 监控 DOM 变化
  dialogObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            // 检查是否是弹窗元素
            if (
              node.classList.contains('graphdialog') ||
              node.classList.contains('litecontextmenu') ||
              node.querySelector('.graphdialog') ||
              node.querySelector('.litecontextmenu')
            ) {
              node.remove()
            }
          }
        })
      }
    })
  })

  // 开始监控整个文档
  dialogObserver.observe(document.body, {
    childList: true,
    subtree: true
  })

  // 定期检查并移除可能出现的弹窗
  dialogCheckInterval = window.setInterval(() => {
    if (isAddingMode.value) {
      const dialogs = document.querySelectorAll(
        '.graphdialog, .litecontextmenu'
      )
      dialogs.forEach((dialog) => {
        dialog.remove()
      })
    }
  }, 100) // 每100ms检查一次
}

// 停止弹窗监控
const stopDialogMonitoring = () => {
  if (dialogObserver) {
    dialogObserver.disconnect()
    dialogObserver = null
  }
  if (dialogCheckInterval) {
    clearInterval(dialogCheckInterval)
    dialogCheckInterval = null
  }
}

const toggleAddingMode = () => {
  isAddingMode.value = !isAddingMode.value
  if (isAddingMode.value) {
    // 启用添加模式时，添加点击监听器
    addCanvasClickListener()
    // 禁用画布的其他交互
    disableCanvasInteractions()
    // 启动弹窗监控
    startDialogMonitoring()
  } else {
    // 退出添加模式时，移除点击监听器
    removeCanvasClickListener()
    // 恢复画布的其他交互
    enableCanvasInteractions()
    // 停止弹窗监控
    stopDialogMonitoring()
  }
}

const addCanvasClickListener = () => {
  // 添加画布点击监听器
  const canvas = document.getElementById('graph-canvas')
  if (canvas) {
    canvas.addEventListener('click', handleCanvasClick, true)
  }

  // 添加全局点击监听器来处理 DOM widget 的点击
  document.addEventListener('click', handleGlobalClick, true)
}

const removeCanvasClickListener = () => {
  // 移除画布点击监听器
  const canvas = document.getElementById('graph-canvas')
  if (canvas) {
    canvas.removeEventListener('click', handleCanvasClick, true)
  }

  // 移除全局点击监听器
  document.removeEventListener('click', handleGlobalClick, true)
}

// 处理全局点击事件，特别是 DOM widget 的点击
const handleGlobalClick = (event: MouseEvent) => {
  if (!isAddingMode.value) {
    return
  }

  const target = event.target as HTMLElement
  if (
    target &&
    (target.classList.contains('dom-widget') ||
      target.closest('.dom-widget') ||
      target.classList.contains('comfy-multiline-input') ||
      target.closest('.comfy-multiline-input'))
  ) {
    // 阻止默认行为，防止弹窗显示
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()

    // 尝试找到对应的节点
    const domWidget = target.closest('.dom-widget')
    if (domWidget) {
      // 获取 DOM widget 的位置信息
      const rect = domWidget.getBoundingClientRect()
      const canvas = document.getElementById('graph-canvas')
      if (canvas && comfyApp.canvas && comfyApp.graph) {
        const canvasRect = canvas.getBoundingClientRect()
        const x = rect.left + rect.width / 2 - canvasRect.left
        const y = rect.top + rect.height / 2 - canvasRect.top

        // 转换为图坐标
        const ds = comfyApp.canvas.ds
        const scale = ds.scale
        const offset = ds.offset
        const graphX = x / scale - offset[0]
        const graphY = y / scale - offset[1]

        // 查找对应的节点
        const clickedNode = comfyApp.graph.getNodeOnPos(graphX, graphY)
        if (clickedNode) {
          // 添加节点的第一个控件
          if (clickedNode.widgets && clickedNode.widgets.length > 0) {
            const firstWidget = clickedNode.widgets[0]
            if (firstWidget.name && firstWidget.type) {
              const inputId = `${clickedNode.id}-${firstWidget.name}`
              selectedInputs.value.add(inputId)
              // 标记为新添加的输入（用于自动类型检测）
              newlyAddedInputs.value.add(inputId)
              inputValues.value[inputId] = firstWidget.value
            }
          }
        }
      }
    }
  }
}

const handleCanvasClick = (event: MouseEvent) => {
  if (!isAddingMode.value) {
    return
  }

  // 检查是否点击了 DOM widget 元素
  const target = event.target as HTMLElement
  if (
    target &&
    (target.classList.contains('dom-widget') ||
      target.closest('.dom-widget') ||
      target.classList.contains('comfy-multiline-input') ||
      target.closest('.comfy-multiline-input'))
  ) {
    return
  }

  // 立即阻止所有默认行为和事件传播，防止弹窗显示
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()

  // 使用LiteGraph的API来获取点击的节点
  const canvas = event.target as HTMLCanvasElement
  if (canvas && comfyApp.canvas && comfyApp.graph) {
    try {
      // 手动计算坐标转换，确保准确性
      const rect = canvas.getBoundingClientRect()
      const clientX = event.clientX - rect.left
      const clientY = event.clientY - rect.top

      // 获取画布的变换状态
      const ds = comfyApp.canvas.ds
      const scale = ds.scale
      const offset = ds.offset

      // 转换为图坐标
      const graphX = clientX / scale - offset[0]
      const graphY = clientY / scale - offset[1]

      // 验证坐标是否有效
      if (isNaN(graphX) || isNaN(graphY)) {
        return
      }

      // 使用LGraph的getNodeOnPos方法获取点击位置的节点
      const clickedNode = comfyApp.graph.getNodeOnPos(graphX, graphY)

      if (clickedNode) {
        // 尝试找到具体点击的控件
        const clickedWidget = clickedNode.getWidgetOnPos(graphX, graphY)

        if (clickedWidget && clickedWidget.name && clickedWidget.type) {
          // 只添加点击的那个控件
          const inputId = `${clickedNode.id}-${clickedWidget.name}`
          selectedInputs.value.add(inputId)
          // 标记为新添加的输入（用于自动类型检测）
          newlyAddedInputs.value.add(inputId)
          // 初始化输入值
          inputValues.value[inputId] = clickedWidget.value

          // 立即保存到工作流数据
          saveWorkflowAppConfig()

          // 强制触发工作流待保存状态
          const activeWorkflow = workflowStore.activeWorkflow
          if (activeWorkflow) {
            activeWorkflow.isModified = true
          }
        } else {
          // 如果没有找到具体控件，添加节点的第一个控件作为备选
          if (clickedNode.widgets && clickedNode.widgets.length > 0) {
            const firstWidget = clickedNode.widgets[0]
            if (firstWidget.name && firstWidget.type) {
              const inputId = `${clickedNode.id}-${firstWidget.name}`
              selectedInputs.value.add(inputId)
              // 标记为新添加的输入（用于自动类型检测）
              newlyAddedInputs.value.add(inputId)
              inputValues.value[inputId] = firstWidget.value

              // 立即保存到工作流数据
              saveWorkflowAppConfig()

              // 强制触发工作流待保存状态
              const activeWorkflow = workflowStore.activeWorkflow
              if (activeWorkflow) {
                activeWorkflow.isModified = true
              }
            }
          }
        }
      }
    } catch (error) {
      // 静默处理错误
    }
  }
}

// 禁用画布交互
const disableCanvasInteractions = () => {
  const canvas = document.getElementById('graph-canvas')
  if (canvas) {
    // 设置十字光标，但保持点击事件
    canvas.style.cursor = 'crosshair'

    // 只禁用特定事件，保留点击事件和滚轮缩放
    canvas.addEventListener('mousedown', preventDefault, true)
    canvas.addEventListener('mousemove', preventDefault, true)
    canvas.addEventListener('mouseup', preventDefault, true)
    // 允许滚轮缩放，不阻止 wheel 事件
    canvas.addEventListener('contextmenu', preventDefault, true)
    canvas.addEventListener('dblclick', preventDefault, true)

    // 添加额外的防护措施，阻止所有可能触发弹窗的事件
    canvas.addEventListener('pointerdown', preventDefault, true)
    canvas.addEventListener('pointerup', preventDefault, true)
    canvas.addEventListener('touchstart', preventDefault, true)
    canvas.addEventListener('touchend', preventDefault, true)
  }
}

// 恢复画布交互
const enableCanvasInteractions = () => {
  const canvas = document.getElementById('graph-canvas')
  if (canvas) {
    // 恢复样式
    canvas.style.cursor = 'default'

    // 移除事件监听器（不包括 wheel，因为我们没有添加它）
    canvas.removeEventListener('mousedown', preventDefault, true)
    canvas.removeEventListener('mousemove', preventDefault, true)
    canvas.removeEventListener('mouseup', preventDefault, true)
    canvas.removeEventListener('contextmenu', preventDefault, true)
    canvas.removeEventListener('dblclick', preventDefault, true)
    canvas.removeEventListener('pointerdown', preventDefault, true)
    canvas.removeEventListener('pointerup', preventDefault, true)
    canvas.removeEventListener('touchstart', preventDefault, true)
    canvas.removeEventListener('touchend', preventDefault, true)
  }
}

// 阻止默认行为的辅助函数
const preventDefault = (event: Event) => {
  // 在添加模式下，阻止所有可能触发弹窗的事件
  if (isAddingMode.value) {
    // 允许点击事件通过，但阻止其他所有事件
    if (event.type === 'click') {
      return // 允许点击事件通过
    }

    // 阻止所有其他事件，包括可能触发弹窗的事件
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }
}

// 从工作流数据加载工作流app配置
const loadWorkflowAppConfig = () => {
  const activeWorkflow = workflowStore.activeWorkflow
  if (!activeWorkflow) {
    return
  }

  // 统一从activeState.extra.workflowApp获取数据
  const workflowAppData = activeWorkflow.activeState.extra?.workflowApp

  if (!workflowAppData) {
    return
  }

  const config = workflowAppData as WorkflowAppConfig

  // 检查节点数据是否已加载
  const nodes = comfyApp.graph?._nodes || []

  if (nodes.length === 0) {
    // 如果节点还没加载，等待一下再试
    setTimeout(() => {
      loadWorkflowAppConfig()
    }, 200)
    return
  }

  // 恢复选中的输入
  selectedInputs.value.clear()
  inputValues.value = {}
  newlyAddedInputs.value.clear() // 清空新添加的标记，因为这些都是已保存的输入

  config.inputs.forEach((input) => {
    selectedInputs.value.add(input.inputId)
    inputValues.value[input.inputId] = input.value
  })
}

// 监听工作流变化，重置输入值并加载配置
watch(
  () => workflowStore.activeWorkflow,
  () => {
    inputValues.value = {}
    selectedInputs.value.clear()

    // 延迟加载配置，确保节点数据已经加载完成
    setTimeout(() => {
      loadWorkflowAppConfig()

      // 如果没有保存的配置，则初始化默认值
      if (selectedInputs.value.size === 0) {
        workflowInputs.value.forEach((input) => {
          inputValues.value[input.id] = input.value
        })
      }
    }, 100) // 延迟100ms确保节点数据加载完成
  },
  { immediate: true }
)

// 监听工作流修改状态，自动保存workflowApp配置
watch(
  () => workflowStore.activeWorkflow?.isModified,
  (isModified) => {
    if (isModified && selectedInputs.value.size > 0 && !isAddingMode.value) {
      saveWorkflowAppConfig()
    }
  }
)

// 监听工作流变化，确保workflowApp数据被保存
watch(
  () => workflowStore.activeWorkflow,
  (newWorkflow) => {
    if (newWorkflow && selectedInputs.value.size > 0 && !isAddingMode.value) {
      // 延迟保存，确保工作流完全加载
      setTimeout(() => {
        saveWorkflowAppConfig()
      }, 100)
    }
  }
)

// 监听画布移动事件，确保workflowApp数据在画布移动时不被丢失
watch(
  () => comfyApp.canvas?.dragging_canvas,
  (isDragging) => {
    if (!isDragging && selectedInputs.value.size > 0 && !isAddingMode.value) {
      // 延迟保存，确保画布移动完全结束
      setTimeout(() => {
        saveWorkflowAppConfig()
      }, 50)
    }
  }
)

// 监听DOM控件变化，确保workflowApp数据在DOM控件交互时被保存
const domWidgetObserver = new MutationObserver(() => {
  if (selectedInputs.value.size > 0 && !isAddingMode.value) {
    saveWorkflowAppConfig()
  }
})

// 监听DOM控件的输入事件
const domInputListener = () => {
  if (selectedInputs.value.size > 0 && !isAddingMode.value) {
    saveWorkflowAppConfig()
  }
}

// 从已保存的配置中恢复选中的输入项
const initializeSelectedInputs = () => {
  const activeWorkflow = workflowStore.activeWorkflow
  if (!activeWorkflow) return

  // 从 workflowApp 配置中获取已保存的输入参数
  if (activeWorkflow.activeState.extra?.workflowApp) {
    const config = activeWorkflow.activeState.extra
      .workflowApp as WorkflowAppConfig

    if (config.inputs && config.inputs.length > 0) {
      config.inputs.forEach((savedInput) => {
        selectedInputs.value.add(savedInput.inputId)
        inputValues.value[savedInput.inputId] = savedInput.value
      })
    }
  }
}

// 启动DOM控件观察
onMounted(() => {
  // 初始化选中的输入项
  initializeSelectedInputs()

  const canvas = document.getElementById('graph-canvas')
  if (canvas) {
    domWidgetObserver.observe(canvas, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'checked', 'selected']
    })

    // 监听DOM控件的输入事件
    canvas.addEventListener('input', domInputListener, true)
    canvas.addEventListener('change', domInputListener, true)
    canvas.addEventListener('blur', domInputListener, true)
  }

  // 监听DOM控件变化事件
  window.addEventListener('domWidgetChanged', handleDomWidgetChanged)
})

// 监听工作流变化，重新初始化选中的输入项
watch(
  () => workflowStore.activeWorkflow,
  (newWorkflow) => {
    if (newWorkflow) {
      // 清空当前选中的输入项
      selectedInputs.value.clear()
      inputValues.value = {}
      // 重新初始化
      initializeSelectedInputs()
    }
  },
  { immediate: false }
)

// 处理DOM控件变化事件
const handleDomWidgetChanged = () => {
  if (selectedInputs.value.size > 0 && !isAddingMode.value) {
    saveWorkflowAppConfig()
  }
}

// 防抖保存机制
let saveTimeout: ReturnType<typeof setTimeout> | null = null

// 保存工作流app配置到工作流数据
const saveWorkflowAppConfig = () => {
  const activeWorkflow = workflowStore.activeWorkflow
  if (!activeWorkflow) return

  // 清除之前的保存定时器
  if (saveTimeout) {
    clearTimeout(saveTimeout)
  }

  // 延迟保存，避免重复保存
  saveTimeout = setTimeout(() => {
    doSaveWorkflowAppConfig()
  }, 100)
}

// 实际执行保存的函数，返回是否真正保存了配置
const doSaveWorkflowAppConfig = (): boolean => {
  const activeWorkflow = workflowStore.activeWorkflow
  if (!activeWorkflow) return false

  // 检查是否和上次数据一样，如果一样就直接结束
  const currentConfig = activeWorkflow.activeState.extra?.workflowApp
  if (currentConfig) {
    const currentInputs = currentConfig.inputs || []
    const currentInputIds = currentInputs.map((input) => input.inputId).sort()
    const selectedInputIds = Array.from(selectedInputs.value).sort()

    // 比较输入ID列表
    if (JSON.stringify(currentInputIds) === JSON.stringify(selectedInputIds)) {
      // 比较每个输入的所有字段（value、options、config、name等）
      let isSame = true
      for (const inputId of selectedInputs.value) {
        const currentInput = currentInputs.find(
          (input) => input.inputId === inputId
        )
        const currentValue = inputValues.value[inputId]
        const workflowInput = workflowInputs.value.find((i) => i.id === inputId)

        if (!currentInput || !workflowInput) {
          isSame = false
          break
        }

        // 比较 value
        if (currentInput.value !== currentValue) {
          isSame = false
          break
        }

        // 比较 name
        if (currentInput.name !== workflowInput.name) {
          isSame = false
          break
        }

        // 比较 options
        if (
          JSON.stringify(currentInput.options) !==
          JSON.stringify(workflowInput.options)
        ) {
          isSame = false
          break
        }

        // 比较 config
        if (
          JSON.stringify(currentInput.config) !==
          JSON.stringify(workflowInput.config)
        ) {
          isSame = false
          break
        }
      }

      if (isSame) {
        return false
      }
    }
  }

  // 基于当前状态重新构建配置，确保包含所有输入参数
  const config: WorkflowAppConfig = {
    inputs: Array.from(selectedInputs.value).map((inputId) => {
      const [nodeId, widgetName] = inputId.split('-')

      // 优先使用已保存的配置数据，确保不会丢失之前的修改
      const existingConfig = activeWorkflow.activeState.extra?.workflowApp
      const existingInput = existingConfig?.inputs?.find(
        (i) => i.inputId === inputId
      )

      // 检查是否有待处理的修改
      const pendingChange = pendingChanges.value.get(inputId)

      if (existingInput) {
        // 如果存在已保存的配置，使用它，但优先使用待处理的修改

        // 获取原始节点数据用于类型检测和选项更新
        const node = comfyApp.graph?._nodes.find(
          (n) => n.id === parseInt(nodeId)
        )
        const widget = node?.widgets?.find((w) => w.name === widgetName)

        // 优先使用待处理修改中的选项，如果没有则使用已保存的选项，最后才从节点获取
        const latestOptions = pendingChange?.options
          ? pendingChange.options
          : existingInput.options && existingInput.options.length > 0
            ? existingInput.options
            : widget?.options
              ? getOptions(existingInput.type, widget.options)
              : existingInput.options

        // 优先使用待处理修改中的类型，如果没有则使用已保存的类型
        // 对于已保存的输入，不进行自动类型检测，直接使用保存的类型
        const finalType = pendingChange?.type
          ? pendingChange.type
          : existingInput.type // 直接使用已保存的类型，不进行自动检测

        // 合并待处理的配置修改（如果有）
        const mergedConfig: Record<string, any> = {
          min: existingInput.config?.min,
          max: existingInput.config?.max,
          step: existingInput.config?.step,
          defaultValue:
            existingInput.config?.defaultValue ?? existingInput.value,
          required: existingInput.config?.required ?? false,
          order: existingInput.config?.order ?? 0,
          llmPrompt: (existingInput.config as any)?.llmPrompt ?? ''
        }

        // 如果有待处理的配置修改，应用它们
        if (pendingChange?.config) {
          Object.assign(mergedConfig, pendingChange.config)
        }

        // 如果有待处理的 order 修改（向后兼容旧代码）
        if (pendingChange?.order !== undefined) {
          mergedConfig.order = pendingChange.order
        }

        return {
          nodeId: existingInput.nodeId,
          widgetName: existingInput.widgetName,
          inputId: existingInput.inputId,
          name: pendingChange?.name ?? existingInput.name, // 优先使用待处理的名称
          type: finalType, // 使用转换后的类型
          value: inputValues.value[inputId] ?? existingInput.value,
          description: existingInput.description,
          placeholder: existingInput.placeholder,
          options: latestOptions, // 使用最新的选项数据
          config: mergedConfig as any // 使用合并后的配置
        }
      }

      // 如果没有已保存的配置，从节点数据构建
      const node = comfyApp.graph?._nodes.find((n) => n.id === parseInt(nodeId))
      const widget = node?.widgets?.find((w) => w.name === widgetName)

      // 检查选项是否包含图片文件，动态调整类型
      const hasImageFiles = (options: any): boolean => {
        if (!options) return false
        const optionArray = Array.isArray(options) ? options : [options]
        return optionArray.some((option) => {
          const value = typeof option === 'string' ? option : option.value
          return value && /\.(png|jpg|jpeg|gif|bmp|webp|svg)$/i.test(value)
        })
      }

      // 优先使用待处理修改中的类型，如果没有则根据选项自动检测，最后使用节点类型
      // 只有在添加输入时才自动检测类型
      const isNewlyAdded = newlyAddedInputs.value.has(inputId)
      const finalType = pendingChange?.type
        ? pendingChange.type
        : isNewlyAdded && hasImageFiles(widget?.options)
          ? 'file.image' // 只对新添加的输入进行自动检测
          : widget?.type || '' // 其他情况直接使用节点类型

      // 构建基础配置
      const baseConfig = {
        min: widget?.options?.min,
        max: widget?.options?.max,
        step: widget?.options?.step,
        defaultValue: widget?.value,
        required: false,
        order: 0,
        llmPrompt: ''
      }

      // 如果有待处理的配置修改，应用它们
      if (pendingChange?.config) {
        Object.assign(baseConfig, pendingChange.config)
      }

      // 如果有待处理的 order 修改（向后兼容旧代码）
      if (pendingChange?.order !== undefined) {
        baseConfig.order = pendingChange.order
      }

      return {
        nodeId,
        widgetName,
        inputId,
        name: pendingChange?.name ?? (widget?.name || ''), // 优先使用待处理的名称
        type: finalType, // 使用转换后的类型
        value: inputValues.value[inputId] ?? widget?.value,
        description: (widget?.options as any)?.description || '',
        placeholder: '',
        options:
          pendingChange?.options ??
          getOptions(finalType, widget?.options || {}), // 优先使用待处理的选项
        config: baseConfig // 使用合并后的配置
      }
    })
  }

  // 确保activeState存在
  if (!activeWorkflow.activeState) {
    activeWorkflow.activeState = {} as any
  }

  // 确保extra存在
  if (!activeWorkflow.activeState.extra) {
    activeWorkflow.activeState.extra = {}
  }

  // 统一保存到activeState.extra.workflowApp
  activeWorkflow.activeState.extra.workflowApp = config

  // 保存成功后，清除新添加的标记（因为已经保存到配置中了）
  newlyAddedInputs.value.clear()

  // 强制触发ChangeTracker检查，确保检测到workflowApp的变化
  if (activeWorkflow.changeTracker) {
    // 强制更新activeState，确保ChangeTracker能检测到变化
    activeWorkflow.changeTracker.activeState = JSON.parse(
      JSON.stringify(activeWorkflow.activeState)
    )
    activeWorkflow.changeTracker.updateModified()
  }

  // 注意：工作流修改状态现在由 saveAllChanges 函数统一管理
  return true
}

// 组件卸载时清理事件监听器
onUnmounted(() => {
  removeCanvasClickListener()
  enableCanvasInteractions() // 确保恢复画布交互
  stopDialogMonitoring() // 清理弹窗监控
  domWidgetObserver.disconnect() // 清理DOM观察器
  window.removeEventListener('domWidgetChanged', handleDomWidgetChanged) // 清理DOM控件变化事件监听器

  // 清理DOM控件输入事件监听器
  const canvas = document.getElementById('graph-canvas')
  if (canvas) {
    canvas.removeEventListener('input', domInputListener, true)
    canvas.removeEventListener('change', domInputListener, true)
    canvas.removeEventListener('blur', domInputListener, true)
  }

  // 清理保存定时器
  if (saveTimeout) {
    clearTimeout(saveTimeout)
    saveTimeout = null
  }
})

// 打开发布弹窗
const goToPublish = () => {
  showPublishDialog.value = true
}

// 处理发布成功
const handlePublishSuccess = (_shareId: string) => {
  // 可以在这里添加成功后的处理逻辑
}
</script>

<style scoped>
.workflow-apps-sidebar-tab {
  background-color: var(--p-surface-card);
}

.workflow-input-item {
  transition: all 0.2s ease;
}

.workflow-input-item:hover {
  border-color: var(--p-primary-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.workflow-input-control :deep(.p-inputtext),
.workflow-input-control :deep(.p-inputnumber),
.workflow-input-control :deep(.p-select),
.workflow-input-control :deep(.p-textarea) {
  width: 100%;
}

/* 使用项目现有的CSS变量系统，模仿WorkflowTabs.vue */
.workflow-input-item {
  background-color: var(--comfy-menu-secondary-bg);
  color: var(--text-color);
}

.workflow-input-item h4 {
  color: var(--text-color);
}

.workflow-input-item .text-xs {
  color: var(--text-color-secondary);
}

.workflow-input-item .bg-surface-100 {
  background-color: var(--comfy-menu-secondary-bg);
  color: var(--text-color);
}

/* 强制覆盖Tailwind类名，使用项目变量 */
.workflow-input-item.bg-surface-0 {
  background-color: var(--comfy-menu-secondary-bg) !important;
}

.workflow-input-item.border-surface-200 {
  border-color: var(--border-color) !important;
}

/* 移除悬停变色效果 */

.workflow-input-item .text-surface-900 {
  color: var(--text-color) !important;
}

.workflow-input-item .text-surface-500 {
  color: var(--text-color-secondary) !important;
}

.workflow-input-item .text-surface-700 {
  color: var(--text-color) !important;
}

/* 表单输入框样式，使用项目CSS变量 */
.workflow-input-field :deep(.p-inputtext),
.workflow-input-field :deep(.p-inputnumber input),
.workflow-input-field :deep(.p-select .p-select-label),
.workflow-input-field :deep(.p-textarea) {
  background-color: var(--comfy-menu-secondary-bg);
  border-color: var(--border-color);
  color: var(--text-color);
}

.workflow-input-field :deep(.p-inputtext:focus),
.workflow-input-field :deep(.p-inputnumber input:focus),
.workflow-input-field :deep(.p-select .p-select-label:focus),
.workflow-input-field :deep(.p-textarea:focus) {
  border-color: var(--p-primary-color);
  box-shadow: 0 0 0 1px var(--p-primary-color);
}

.workflow-input-field :deep(.p-inputtext::placeholder),
.workflow-input-field :deep(.p-textarea::placeholder) {
  color: var(--text-color-secondary);
}

/* 下拉菜单样式 */
.workflow-input-field :deep(.p-select-dropdown) {
  background-color: var(--comfy-menu-secondary-bg);
  border-color: var(--border-color);
}

.workflow-input-field :deep(.p-select-option) {
  color: var(--text-color);
}

.workflow-input-field :deep(.p-select-option:hover) {
  background-color: var(--comfy-menu-hover-bg);
}

/* 切换开关样式 */
.workflow-input-field :deep(.p-toggleswitch) {
  background-color: var(--border-color);
}

.workflow-input-field :deep(.p-toggleswitch.p-toggleswitch-checked) {
  background-color: var(--p-primary-color);
}

/* 操作按钮样式 */
.edit-button,
.delete-button {
  padding: 4px !important;
  min-width: 24px !important;
  height: 24px !important;
  border-radius: 4px !important;
}

/* 删除按钮悬停样式 - 带圆角的方形 */
.delete-button:hover {
  background-color: var(--p-red-100) !important;
  border-radius: 4px !important;
}

/* 调整表单高度 */
.workflow-input-control :deep(.p-inputtext),
.workflow-input-control :deep(.p-inputnumber input),
.workflow-input-control :deep(.p-select .p-select-label),
.workflow-input-control :deep(.p-textarea) {
  height: 32px !important;
  min-height: 32px !important;
}

.workflow-input-control :deep(.p-textarea) {
  height: auto !important;
  min-height: 32px !important;
}

/* 移除h4标签的上下margin */
.workflow-input-item h4 {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

/* 添加模式提示框样式 - 适配黑色模式 */
.adding-mode-tip {
  background-color: var(--comfy-menu-secondary-bg);
  border: 1px solid var(--border-color);
  color: var(--text-color);
}

.adding-mode-content {
  color: var(--text-color);
}
</style>
